import React, { useEffect, useState } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { useTheme } from '../contexts/ThemeContext';
import logoPositivoUrl from '../assets/logos/SVG/POSITIVO/logo scribane_positivo micro simple adjusted.svg?url';
import logoBlancoUrl from '../assets/logos/SVG/BLANCO/logo scribane_blanco micro simple.svg?url';
import { LoadingRing, getLoadingRingDiameterForContent } from './LoadingRing';

const LOADING_DOTS_INTERVAL_MS = 450;
const DOCUMENT_LOADING_SPIN_DURATION_MS = 1600;
const DOCUMENT_LOADING_LOGO_PULSE_DURATION_MS = 3500;
const DOCUMENT_LOADING_LOGO_HEIGHT_PX = 56;
const DOCUMENT_LOADING_RING_DIAMETER_PX = getLoadingRingDiameterForContent(
  DOCUMENT_LOADING_LOGO_HEIGHT_PX,
);

export const loadingTextBase = (text: string): string => text.replace(/\.+$/, '').trimEnd();

type DocumentLoadingScreenVariant = 'panel' | 'fullscreen';

interface DocumentLoadingScreenProps {
  text?: string;
  variant?: DocumentLoadingScreenVariant;
}

const containerClassName: Record<DocumentLoadingScreenVariant, string> = {
  panel: 'flex h-full min-h-0 w-full flex-1 items-center justify-center',
  fullscreen: 'flex min-h-screen w-full items-center justify-center',
};

export const DocumentLoadingScreen: React.FC<DocumentLoadingScreenProps> = ({
  text,
  variant = 'panel',
}) => {
  const { t } = useLocale();
  const { resolvedTheme } = useTheme();
  const loadingLabel = text ?? t('loading');
  const baseText = loadingTextBase(loadingLabel);
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setDotCount((current) => (current >= 3 ? 1 : current + 1));
    }, LOADING_DOTS_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, []);

  const logoUrl = resolvedTheme === 'dark' ? logoBlancoUrl : logoPositivoUrl;

  return (
    <div
      className={containerClassName[variant]}
      role="status"
      aria-live="polite"
      aria-label={loadingLabel}
      data-testid="document-loading-screen"
      data-variant={variant}
    >
      <style>{`
        @keyframes document-loading-logo-pulse {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
      `}</style>
      <div className="flex flex-col items-center gap-5">
        <div
          className="relative flex items-center justify-center"
          style={{
            width: DOCUMENT_LOADING_RING_DIAMETER_PX,
            height: DOCUMENT_LOADING_RING_DIAMETER_PX,
          }}
        >
          <LoadingRing
            diameterPx={DOCUMENT_LOADING_RING_DIAMETER_PX}
            activeColor="var(--scribane-green)"
            trackColor={resolvedTheme === 'dark' ? 'var(--muted)' : '#e5e7eb'}
            spinDurationMs={DOCUMENT_LOADING_SPIN_DURATION_MS}
            className="absolute inset-0"
          />
          <img
            src={logoUrl}
            alt="Scribane"
            className="relative z-10 w-auto"
            style={{
              height: DOCUMENT_LOADING_LOGO_HEIGHT_PX,
              opacity: 0,
              animation: `document-loading-logo-pulse ${DOCUMENT_LOADING_LOGO_PULSE_DURATION_MS}ms ease-in-out infinite`,
            }}
          />
        </div>
        <p className="text-center text-sm text-muted-foreground" data-testid="loading-dots">
          <span className="relative inline-block">
            <span data-testid="loading-dots-label">{baseText}</span>
            <span
              className="absolute left-full top-0 inline-block w-[1.5em] text-left"
              aria-hidden="true"
              data-testid="loading-dots-ellipsis"
            >
              {'.'.repeat(dotCount)}
            </span>
          </span>
        </p>
      </div>
    </div>
  );
};
